import { useState } from "react";
import { Layers, Plus, Check, Trash2, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function SessionSwitcher({ sessions, currentId, onSwitch, onCreate, onDelete }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const [open, setOpen] = useState(false);
  const current = sessions.find((s) => s.id === currentId) || {};
  const label = (current.eventName || "").trim() || (current.started ? "Live session" : "New session");

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="session-switcher"
            className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform" style={{ color: "var(--text)" }}>
            <Layers className="w-4 h-4 text-primary-c" />
            <span className="truncate max-w-[150px]">{label}</span>
            {sessions.length > 1 && (
              <span className="mono text-[10px] rounded-full px-1.5" style={{ background: "var(--primary)", color: "var(--on-primary)" }}>{sessions.length}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="glass w-80 p-2" style={{ background: "var(--surface-glass)" }} data-testid="session-switcher-content">
          <div className="text-[10px] uppercase tracking-wider px-2 py-1.5" style={{ color: "var(--text-2)" }}>Your sessions</div>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="session-list">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`session-item-${s.id}`}
                className={`rounded-lg px-2.5 py-2 flex items-center gap-2 ${s.id === currentId ? "glow-border" : ""}`}
                style={{ background: s.id === currentId ? "var(--surface)" : "transparent" }}>
                <button data-testid={`session-switch-${s.id}`} onClick={() => { onSwitch(s.id); setOpen(false); }} className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-bold truncate flex items-center gap-1.5">
                    {s.id === currentId && <Check className="w-3.5 h-3.5 text-primary-c shrink-0" />}
                    {(s.eventName || "").trim() || "Untitled session"}
                  </div>
                  <div className="text-[10px] mono flex items-center gap-2 mt-0.5" style={{ color: "var(--text-2)" }}>
                    <span style={s.started ? { color: "var(--primary-ink)" } : {}}>{s.started ? "\u25CF Live" : "Setup"}</span>
                    <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{s.checkedIn}/{s.playerCount}</span>
                    {s.matches > 0 && <span>{s.matches} game{s.matches === 1 ? "" : "s"}</span>}
                  </div>
                </button>
                <button data-testid={`session-delete-${s.id}`} onClick={() => setConfirmDel(s)}
                  className="w-7 h-7 rounded-full glass flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
              </div>
            ))}
          </div>
          <button data-testid="session-create" onClick={() => { onCreate(); setOpen(false); }}
            className="btn-glow rounded-lg w-full mt-2 py-2 text-sm font-bold flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" /> New session</button>
        </PopoverContent>
      </Popover>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="session-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this session?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>
              "{(confirmDel?.eventName || "").trim() || "Untitled session"}" and all its players, courts and match history will be permanently removed. Your other sessions are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="session-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="session-delete-confirm"
              onClick={() => { onDelete(confirmDel.id); setConfirmDel(null); }}
              className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
